import { useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { usePermissions } from '@app/contexts/PermissionContext';
import { MessageTemplateListItem, MessageTemplatesListParams } from '@app/models/messageTemplates';
import { createMessageTemplate, deleteMessageTemplate, getMessageTemplate, getMessageTemplates, sendTestMessageTemplate, setDefaultMessageTemplate } from '@app/services/messageTemplates';
import { getApiErrorResponse } from '@app/services/api';
import { MessageTemplateChannel } from '@app/utils/consts';
import { useDebounce } from '@app/utils/use-debounce';

type SortField = NonNullable<MessageTemplatesListParams['sortBy']>;

const NAME_MAX_LENGTH = 191;

const getErrorMessage = (error: unknown, fallback: string) => getApiErrorResponse(error).data?.message || fallback;

export const useMessageTemplates = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const canEdit = can('createCampaigns') || can('createAutomations');

  // The channel lives in the URL so returning from the edit page keeps the tab
  const [searchParams, setSearchParams] = useSearchParams();
  const channel = searchParams.get('channel') === MessageTemplateChannel.SMS ? MessageTemplateChannel.SMS : MessageTemplateChannel.EMAIL;

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortBy, setSortBy] = useState<SortField | undefined>();
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearch = useDebounce(searchValue.trim(), 500);

  const [deleteTarget, setDeleteTarget] = useState<MessageTemplateListItem | undefined>();
  const [defaultTarget, setDefaultTarget] = useState<MessageTemplateListItem | undefined>();

  const params: MessageTemplatesListParams = {
    channel,
    search: debouncedSearch || undefined,
    page,
    limit,
    sortBy,
    sortOrder: sortBy ? sortOrder : undefined,
  };

  const { data: templatesData, isLoading } = useQuery({
    queryKey: ['message-templates', 'list', params],
    queryFn: () => getMessageTemplates(params).then((res) => res.data),
    placeholderData: keepPreviousData,
  });

  // Usage list for the delete confirmation
  const { data: deleteTargetDetails, isLoading: isDeleteTargetLoading } = useQuery({
    queryKey: ['message-templates', 'item', deleteTarget?.id],
    queryFn: () => getMessageTemplate(deleteTarget!.id).then((res) => res.data),
    enabled: Boolean(deleteTarget),
  });

  useEffect(() => {
    setPage(1);
  }, [channel, debouncedSearch]);

  // Deleting the last row of a page leaves it empty, so step back
  useEffect(() => {
    if (templatesData && templatesData.templates.length === 0 && page > 1) {
      setPage(Math.max(templatesData.totalPages, 1));
    }
  }, [templatesData, page]);

  const invalidateTemplates = () => queryClient.invalidateQueries({ queryKey: ['message-templates'] });

  const duplicateMutation = useMutation({
    mutationFn: ({ name, channel, subject, logo, heading, message, buttonText, buttonColor }: MessageTemplateListItem) =>
      createMessageTemplate({
        name: `${name} (copy)`.slice(0, NAME_MAX_LENGTH),
        channel,
        ...(subject !== null && { subject }),
        ...(logo !== null && { logo }),
        ...(heading !== null && { heading }),
        ...(message !== null && { message }),
        ...(buttonText !== null && { buttonText }),
        ...(buttonColor !== null && { buttonColor }),
      }),
    onSuccess: () => {
      toast.success('Template duplicated');
      invalidateTemplates();
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Failed to duplicate template')),
  });

  const setDefaultMutation = useMutation({
    mutationFn: (id: string) => setDefaultMessageTemplate(id),
    onSuccess: () => {
      toast.success('Default template updated');
      invalidateTemplates();
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Failed to update default template')),
    onSettled: () => setDefaultTarget(undefined),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteMessageTemplate(id),
    onSuccess: () => {
      toast.success('Template deleted');
      invalidateTemplates();
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Failed to delete template')),
    onSettled: () => setDeleteTarget(undefined),
  });

  const sendTestMutation = useMutation({
    mutationFn: (template: MessageTemplateListItem) => sendTestMessageTemplate(template.id),
    onSuccess: ({ data }) => toast.success(`Test email sent to ${data.email}`),
    onError: (error) => toast.error(getErrorMessage(error, 'Failed to send the test email')),
  });

  const handleChannelChange = (value: string) => {
    setSearchParams(value === MessageTemplateChannel.SMS ? { channel: MessageTemplateChannel.SMS } : {}, { replace: true });
  };

  const handleSort = (field: string) => {
    const sortField = field as SortField;

    if (sortBy === sortField) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(sortField);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const handleLimitChange = (newLimit?: number) => {
    setLimit(newLimit ?? 10);
    setPage(1);
  };

  const createTemplate = () => navigate(`/templates/create?channel=${channel}`);

  const openTemplate = (template: MessageTemplateListItem) => navigate(`/templates/${template.id}`);

  return {
    canEdit,
    channel,
    handleChannelChange,
    templatesData,
    isLoading,
    page,
    setPage,
    handleLimitChange,
    sortBy,
    sortOrder,
    handleSort,
    searchValue,
    setSearchValue,
    isSearching: Boolean(debouncedSearch),
    createTemplate,
    openTemplate,
    duplicateTemplate: duplicateMutation.mutate,
    isDuplicating: duplicateMutation.isPending,
    sendTestEmail: sendTestMutation.mutate,
    isSendingTest: sendTestMutation.isPending,
    defaultTarget,
    setDefaultTarget,
    confirmSetDefault: () => defaultTarget && setDefaultMutation.mutate(defaultTarget.id),
    isSettingDefault: setDefaultMutation.isPending,
    deleteTarget,
    setDeleteTarget,
    deleteTargetDetails: deleteTargetDetails?.id === deleteTarget?.id ? deleteTargetDetails : undefined,
    isDeleteTargetLoading,
    confirmDelete: () => deleteTarget && deleteMutation.mutate(deleteTarget.id),
    isDeleting: deleteMutation.isPending,
  };
};
